/**
 * Writes the app's pulse, and reads the last run's on the way in.
 *
 * See heartbeat.ts for why this exists. Nothing renders; it keeps a short
 * rolling record of whether the main thread was answering its timer on time, so
 * a freeze can be read afterwards rather than reasoned about.
 *
 * The previous run's verdict is taken ONCE, at module load, before this run
 * starts writing over it. Doing it in an effect would be a race with the very
 * first tick, and the evidence we are collecting is the evidence we would lose.
 */
import { useEffect, useRef } from 'react';
import { useGnssStore } from '@/store/gnss';
import { useShellStore } from '@/store/shell';
import { loadRendererDeaths } from './rendererDeaths';
import { useChartPlotterStore, PRIMARY_TRACK_ID } from '@/store';
import {
  HEARTBEAT_LS,
  HEARTBEAT_MS,
  diagnoseRun,
  formatTicks,
  freezeSummary,
  parseTicks,
  pushTick,
  readHeap,
  serialiseTicks,
  type HeartbeatTick,
  type RunVerdict,
} from './heartbeat';

function readStored(): HeartbeatTick[] {
  try {
    return parseTicks(globalThis.localStorage?.getItem(HEARTBEAT_LS) ?? null);
  } catch {
    return [];
  }
}

/**
 * How the run before this one ended, and its last few pulses. Captured at
 * import time, before anything overwrites them.
 *
 * Wrapped, and it matters more here than almost anywhere. This module is
 * imported by the shell and by the bug-report context, so it is loaded on the
 * way up on every single launch. Code at module scope that throws does not
 * degrade a feature, it stops the whole bundle, and since the input is a value
 * read off disk, a bad one would do that on EVERY launch from then on. That is
 * a diagnostic tool bricking the app it exists to diagnose, which is about the
 * worst way to repay the trust of putting it in a safety app.
 */
function readPrevious(): { ticks: HeartbeatTick[]; verdict: RunVerdict } {
  try {
    const ticks = readStored();
    return { ticks, verdict: diagnoseRun(ticks, Date.now()) };
  } catch {
    return {
      ticks: [],
      verdict: {
        kind: 'unknown',
        summary: 'previous run unreadable',
        lastTickMs: null,
        gapMs: null,
      },
    };
  }
}

const { ticks: previousTicks, verdict: previousVerdict } = readPrevious();

/** The previous run's verdict, for the bug-report context. */
export function lastRunVerdict(): RunVerdict {
  return previousVerdict;
}

/** The previous run's final pulses, oldest first, for the bug-report context. */
export function lastRunTail(): string[] {
  return formatTicks(previousTicks);
}

/**
 * Whether the page has been frozen in the background, and for how long.
 *
 * Reads the previous run and this one together, because a freeze in the middle
 * of an otherwise healthy run is exactly the case that hid for days: the run
 * ends cleanly, the track fills itself in from queued fixes, and nothing
 * anywhere says that no alarm was checked for twenty minutes.
 */
export function freezeLine(): string | null {
  return freezeSummary([...previousTicks, ...currentTicks]);
}

/**
 * THIS run's pulses. Module-scope rather than component state so the
 * diagnostics panel can read them without the runner having to publish into a
 * store on every beat, which would be a store write every ten seconds for the
 * benefit of a screen that is almost never open.
 */
let currentTicks: HeartbeatTick[] = [];

/** This run's last few pulses, so the panel can show a watch that is healthy. */
export function liveTail(): string[] {
  return formatTicks(currentTicks);
}

export function HeartbeatRunner() {
  const ring = useRef<HeartbeatTick[]>([]);

  useEffect(() => {
    // Due time is tracked explicitly rather than taken from the interval, because
    // the whole point is measuring how far the interval slipped. setInterval
    // quietly drops beats when the thread is busy, so counting them would hide
    // exactly the thing we are here to see.
    let due = Date.now() + HEARTBEAT_MS;

    // A pulse arriving this late means the page has just THAWED from a long
    // freeze. Announce it, because the app comes back with stale everything:
    // tile fetches MapLibre gave up on mid-freeze, a residency pass that never
    // ran, weather for a viewport from an hour ago. On 10 Aug that read as
    // "took ages to reopen and now the charts haven't loaded"
    // (fb-msn4uxnb-4); the listeners kick the refetches instead.
    const THAW_KICK_MS = 60_000;

    const tick = () => {
      const now = Date.now();
      const lagMs = Math.max(0, now - due);
      due = now + HEARTBEAT_MS;
      if (lagMs > THAW_KICK_MS) {
        try {
          window.dispatchEvent(new CustomEvent('acp:thawed', { detail: { lagMs } }));
        } catch {
          // An event that cannot be dispatched must never stop the pulse.
        }
      }

      const lastFix = useGnssStore.getState().lastFixWallMs;
      const track = useChartPlotterStore.getState().tracks[PRIMARY_TRACK_ID];
      const { heapMB, heapLimitMB } = readHeap();

      ring.current = pushTick(ring.current, {
        t: now,
        lagMs,
        visible: globalThis.document?.visibilityState !== 'hidden',
        fixAgeMs: lastFix == null ? null : now - lastFix,
        heapMB,
        heapLimitMB,
        trackPoints: track?.points.length ?? 0,
      });
      currentTicks = ring.current;

      try {
        globalThis.localStorage?.setItem(HEARTBEAT_LS, serialiseTicks(ring.current));
      } catch {
        // A full or unavailable localStorage must never take the app down. Losing
        // the black box is a shame; losing the anchor watch is not survivable.
      }
    };

    tick();
    const id = window.setInterval(tick, HEARTBEAT_MS);
    // Whether Android has been taking our renderer away is written on the native
    // side, so it has to be read off disk. Once, on the way up.
    void loadRendererDeaths();
    return () => window.clearInterval(id);
  }, []);

  // Say so, once, when the run before this one did not end well. Otherwise the
  // evidence sits in a bug report nobody knows to send: a skipper who came back
  // to a dead app in the morning has no reason to think the app has anything to
  // tell them about it. Delayed past the boot rush so it is not lost among the
  // chart loading.
  useEffect(() => {
    if (previousVerdict.kind !== 'starved' && previousVerdict.kind !== 'stalled') return;
    const at = previousVerdict.lastTickMs
      ? new Date(previousVerdict.lastTickMs).toTimeString().slice(0, 5)
      : 'some point';
    const id = window.setTimeout(() => {
      useShellStore
        .getState()
        .showToast(`The app stopped responding at ${at}. Send a bug report and it will carry why.`);
    }, 4000);
    return () => window.clearTimeout(id);
  }, []);

  return null;
}
